import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/services/users/users.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { UserInfo } from '../../../models/user-info.model';
import { entAnimations } from 'src/app/animations';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { CustomValidators } from 'src/app/utils/validators';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { UserSelfPasswordChange } from 'src/app/models/user-password-change.model';
import { environment } from 'src/environments/environment';
import { KeycloakService } from 'src/app/services/keycloak/keycloak.service';

@Component({
  selector: 'app-password-change',
  templateUrl: './password-change.component.html',
  styleUrls: ['./password-change.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class PasswordMustChangeComponent implements OnInit {
  private selectedUser: UserInfo = new UserInfo();
  isBusy = false;
  error: Error = { error: { message: '', code: '' } };
  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  // Pwd
  securityform: FormGroup;
  isPwdUpdated = false;
  isErrorUpdatePwd = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _keycloakService: KeycloakService,
    private _translateService: TranslateService,
    private _userService: UsersService,
    private _router: Router,
    private _localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.isBusy = false;
    this.getUser();

    this.securityform = this._formBuilder.group(
      {
        oldpassword: ['', [Validators.required]],
        password: [
          '',
          [
            Validators.required,
            CustomValidators.passwordRestriction(),
            CustomValidators.passwordInterdiction(),
            CustomValidators.passwordNameValidator(
              this.selectedUser.officialFirstName,
              this.selectedUser.officialLastName
            ),
            CustomValidators.passwordSameAccountNameValidator(
              this.selectedUser.samAccountName
            ),
            Validators.minLength(10),
            Validators.maxLength(50),
          ],
        ],
        passwordConfirmation: ['', [Validators.required]],
      },
      { validators: [CustomValidators.confirmPasswords()] }
    );
  }

  getErrorMessageOldPassword(): string {
    return this.securityform.get('oldpassword').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.securityform.get('oldpassword').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.securityform.get('oldpassword').hasError('oldpasswordmatch')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.securityform.get('oldpassword').hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.securityform.get('oldpassword').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.securityform.get('oldpassword').hasError('maxlength')
                ? this._translateService.instant('ERROR.MAX')
                : '';
  }

  getErrorMessage(): string {
    return this.securityform.get('password').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.securityform.get('password').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.securityform.get('password').hasError('oldpasswordmatch')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.securityform.get('password').hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.securityform.get('password').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.securityform
                    .get('password')
                    .hasError('passwordContainSamAccountName')
                ? this._translateService.instant('ERROR.CONTAINSAMACCOUNTNAME')
                : this.securityform.get('password').hasError('maxlength')
                  ? this._translateService.instant('ERROR.MAX')
                  : '';
  }

  logout(): void {
    this._userService.clearStorageAndLogout();
    this._keycloakService.logout(environment.logoutURL);
    this._keycloakService.clearToken();
  }

  private fetchSecurityFormData(): UserSelfPasswordChange {
    return {
      password: this.securityform.get('oldpassword').value,
      newPassword: this.securityform.get('password').value,
    };
  }

  private getUser(): void {
    this.isBusy = true;
    this._userService.getCurrentUserFromStorage().subscribe({
      next: u => {
        this.selectedUser = u;
        this.isBusy = false;
      },
      error: error => {
        let customError: Error = { error: undefined };
        if (error.error) {
          customError = error.error;
        }

        if (customError.error !== undefined) {
          if (customError.error.code == 'ERR403') {
            this._router.navigate(['/errors/error-403']);
          } else {
            this._router.navigate(['/errors/error-404']);
          }
        }
        this.isBusy = false;
      },
    });
  }

  async changePwd(): Promise<void> {
    this.error = { error: { message: '', code: '' } };
    this.isErrorUpdatePwd = false;
    this.isPwdUpdated = false;

    try {
      this.isBusy = true;
      await lastValueFrom(
        this._userService.changePassword(
          this.selectedUser.oid,
          this.fetchSecurityFormData()
        )
      ).then(() => {
        this.isPwdUpdated = true;
        this._localStorageService.removeItem('currentUser');
        window.location.reload();
      });
    } catch (error) {
      if (error.statusText == 'Unknown Error') {
        this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          this.error = error.error;
        } else if (error.error === undefined) {
          this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
        }
      }

      this.isErrorUpdatePwd = true;
      this.isPwdUpdated = false;
    } finally {
      this.securityform.reset();
      this.isBusy = false;
    }
  }
}
